// @ts-nocheck

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { type StickyMatchActionsRowContext } from '../../constants/match';
import { usePrevious } from '../../hooks';
import useMatchBetFiltersContext from '../../hooks/useMatchBetFiltersContext';
import { MatchBetFiltersPatternsPrefixes, getMatchBetFilterPattern } from '../../types/matchBetFilters';
import { MatchBetFiltersHandlerContext } from '../../utils/contexts';
import { type MatchBetFilter } from '../../utils/contexts';
import { extrudePropertiesFromHash } from '../../utils/location';
import MatchBetFilters from '../MatchBetFilters';

const updateFiltersHistory = (relativePath, filters, setFiltersHistory, onBackClick) => {
  if (setFiltersHistory) {
    let nbGoBackRequired = 0;

    setFiltersHistory((prev) => {
      const next = [filters];
      let nextSubFilters = filters;

      for (let i = 1; i < prev.length; i++) {
        const nextParentId = relativePath[i - 1];
        const nextFilter = nextSubFilters?.find((filter) => filter.id === nextParentId);

        nextSubFilters = nextFilter?.filters;

        if (nextSubFilters) {
          next[i] = nextSubFilters;
        } else {
          nbGoBackRequired = relativePath.length + 1 - i;
          break;
        }
      }

      for (let j = 0; j < nbGoBackRequired; j++) {
        onBackClick();
      }

      return next;
    });
  }
};

type MatchBetFiltersHandlerProps = {
  className?: string;
  filters: Array<MatchBetFilter>;
  onSelectionChange: (filter: string | null) => void;
  defaultFilterId?: string;
  untoggleable?: boolean;
  noAutoScroll?: boolean;
  context?: string;
  locationHash?: string;
  context?: StickyMatchActionsRowContext;
};

const MatchBetFiltersHandler = ({
  className,
  filters,
  onSelectionChange,
  defaultFilterId,
  untoggleable = false,
  noAutoScroll = false,
  context = '',
  locationHash,
}: MatchBetFiltersHandlerProps) => {
  const filtersRef = useRef();
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const previousFilters = usePrevious(filters);
  const previousFiltersRef = useRef();
  useEffect(() => {
    previousFiltersRef.current = previousFilters;
  }, [previousFilters]);
  const [filtersHistory, setFiltersHistory] = useState([filters]);
  const relativePath = useMemo(
    () => filtersHistory?.map((filters) => filters?.[0]?.parentId).filter((k) => k),
    [filtersHistory],
  );
  const relativePathRef = useRef();
  useEffect(() => {
    relativePathRef.current = relativePath;
  }, [relativePath]);

  const [currentFilter, setCurrentFilter] = useState<MatchBetFilter | null>(
    (() => {
      if (defaultFilterId) {
        return filters.find((f) => f.id === defaultFilterId) ?? null;
      }

      if (untoggleable && filters.length) {
        return filters[0];
      }

      return null;
    })(),
  );

  const smartSetCurrentFilter = useCallback(
    (next) => {
      setCurrentFilter((prev) => {
        const nextValue = typeof next === 'function' ? next(prev) : next;

        if (untoggleable && !nextValue) {
          const defaultFilter = filtersRef.current?.[0];

          if (defaultFilter) {
            return defaultFilter;
          }
        }

        return nextValue;
      });
    },
    [untoggleable],
  );

  const { isHotFilterSelected, setIsHotFilterSelected, setPartialOnHotFilterClicked } = useMatchBetFiltersContext();

  useLayoutEffect(() => {
    if (!isHotFilterSelected && setPartialOnHotFilterClicked) {
      setPartialOnHotFilterClicked(() => () => {
        smartSetCurrentFilter(null);
      });
    } else {
      if (currentFilter && setIsHotFilterSelected) {
        setIsHotFilterSelected(false);
      }

      if (setPartialOnHotFilterClicked) {
        setPartialOnHotFilterClicked(() => () => {});
      }
    }
  }, [currentFilter, isHotFilterSelected, setIsHotFilterSelected, setPartialOnHotFilterClicked, smartSetCurrentFilter]);

  const currentSubFilters = useMemo(() => filtersHistory?.[filtersHistory.length - 1], [filtersHistory]);
  const currentDepth = useMemo(() => filtersHistory?.length, [filtersHistory]);

  const onFilterClick = useCallback(
    (i) => {
      const selectedFilter = currentSubFilters[i];
      let nextData = selectedFilter?.filters ? [...filtersHistory, selectedFilter.filters] : [...filtersHistory];

      if (currentFilter?.id === currentSubFilters[i]?.id) {
        const parentFilters = filtersHistory[filtersHistory.length - 2];

        if (parentFilters) {
          const parentFilter =
            parentFilters
              ?.filter((k) => k)
              ?.find((filter) => !!currentFilter?.parentId && filter.id === currentFilter?.parentId) ?? null;

          smartSetCurrentFilter(parentFilter);
        } else if (!untoggleable) {
          nextData = [filters];
          smartSetCurrentFilter(null);
        }
      } else {
        smartSetCurrentFilter(currentSubFilters[i]);
      }

      setFiltersHistory(nextData);
    },
    [
      currentSubFilters,
      filtersHistory,
      currentFilter?.id,
      currentFilter?.parentId,
      untoggleable,
      smartSetCurrentFilter,
      filters,
    ],
  );

  const onBackClick = useCallback(() => {
    if (currentFilter) {
      const previousFilters = filtersHistory.slice(0, -1);

      const { filters, parentId } = currentFilter;
      const parentFilters = filtersHistory[filtersHistory.length - 2];
      const grandParentFilters = filtersHistory[filtersHistory.length - 3];

      if (!filters) {
        const parent = parentFilters?.find((filter) => !!parentId && filter.id === parentId) ?? null;

        if (parent) {
          const { parentId: grandParentId } = parent;

          setFiltersHistory(previousFilters);
          smartSetCurrentFilter(
            grandParentFilters?.find((filter) => !!grandParentId && filter.id === grandParentId) ?? null,
          );
        }
      } else {
        setFiltersHistory(previousFilters);
        smartSetCurrentFilter(grandParentFilters?.find((filter) => !!parentId && filter.id === parentId) ?? null);
      }
    }
  }, [currentFilter, filtersHistory, smartSetCurrentFilter]);

  const onBackClickRef = useRef();

  useEffect(() => {
    onBackClickRef.current = onBackClick;
  }, [onBackClick]);

  useEffect(() => {
    if (!!previousFiltersRef.current && relativePathRef.current && onBackClickRef.current) {
      updateFiltersHistory(relativePathRef.current, filters, setFiltersHistory, onBackClickRef.current);
    }
  }, [filters]);

  useEffect(() => {
    if (!defaultFilterId) {
      smartSetCurrentFilter((_currentFilter) => {
        let lastExistingFilter = null;

        for (let i = 0; i < filtersHistory.length; i++) {
          const currFilters = filtersHistory[i];

          if (relativePath.length) {
            const targetedFilterId = relativePath[i];
            lastExistingFilter = currFilters.find((filter) => filter.id === targetedFilterId) ?? lastExistingFilter;
            const isCurrentDirectTargetChild = !!lastExistingFilter?.filters?.find(
              (filter) => filter?.id && _currentFilter?.id && filter?.id === _currentFilter?.id,
            );
            if (lastExistingFilter?.id === _currentFilter?.id || isCurrentDirectTargetChild) {
              return _currentFilter;
            }
          } else if (_currentFilter && currFilters.find((filter) => filter.id === _currentFilter.id)) {
            return _currentFilter;
          }
        }

        return lastExistingFilter;
      });
    }
  }, [relativePath, filtersHistory, defaultFilterId, smartSetCurrentFilter]);

  useEffect(() => {
    smartSetCurrentFilter((prev) => {
      const filtersUnref = filtersRef.current;

      if (defaultFilterId) {
        return filtersUnref?.find((f) => f.id === defaultFilterId) ?? null;
      }

      if (untoggleable && filtersUnref?.length) {
        return filtersUnref?.[0] ?? null;
      }

      return prev;
    });
  }, [defaultFilterId, smartSetCurrentFilter, untoggleable]);

  useEffect(() => {
    let finalFilterId = relativePath.join();
    // Si le filtre courant est le dernier sous filtre, on concatene son id
    if (currentFilter && !currentFilter.filters?.length) {
      finalFilterId += currentFilter.id;
    }

    onSelectionChange(finalFilterId);
  }, [currentFilter, onSelectionChange, filtersHistory, relativePath]);

  const isInitialFilterSet = useRef(false);
  useEffect(() => {
    if (isInitialFilterSet.current) {
      return;
    }

    const { bc: betCategoryId, p: playerId } = extrudePropertiesFromHash(locationHash);

    if (betCategoryId) {
      const betCategoryFilterId = getMatchBetFilterPattern(MatchBetFiltersPatternsPrefixes.BET_CATEGORY, betCategoryId);
      const categoryFilter = currentSubFilters.find((filter) => filter.id === betCategoryFilterId);

      if (categoryFilter) {
        setCurrentFilter(categoryFilter);

        if (categoryFilter.filters) {
          setFiltersHistory([...filtersHistory, categoryFilter.filters]);
        }

        if (playerId) {
          const playerFilterId = getMatchBetFilterPattern(MatchBetFiltersPatternsPrefixes.PLAYER_ID, playerId);
          const playerFilter = categoryFilter.filters?.find((filter) => filter.id === playerFilterId);

          if (playerFilter) {
            setCurrentFilter(playerFilter);
            isInitialFilterSet.current = true;
          }
        } else {
          isInitialFilterSet.current = true;
        }
      }
    }
  }, [locationHash, currentSubFilters, filtersHistory]);

  const forwardedState = useMemo(() => ({ currentFilter }), [currentFilter]);

  return (
    <MatchBetFiltersHandlerContext.Provider value={forwardedState}>
      <MatchBetFilters
        className={className}
        filters={currentSubFilters}
        onFilterClick={onFilterClick}
        onBackClick={currentDepth > 1 ? onBackClick : undefined}
        noAutoScroll={noAutoScroll}
        context={context}
      />
    </MatchBetFiltersHandlerContext.Provider>
  );
};

export default MatchBetFiltersHandler;
